import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { generatePath, useNavigate } from 'react-router-dom';
import { selectedChange, tableChange } from '../../actions/table.actions';
import Table from '../../components/tables/Table';
import { usePreferences } from '../../hooks/usePreferences.hook';
import { useTableResource } from '../../hooks/useTableResource.hook';
import { currentUserSelector } from '../../selectors/auth.selectors';
import { tableSelectedSelector } from '../../selectors/resources.selectors';

const ResourceTable = ({ resource, actions, legend, onRowClick, errorHandlers }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector(currentUserSelector);
  const preferences = usePreferences(user, resource);
  const { isFetched, sort, fields, size, onEnableField, onOrderField, onPageSizeChange, onSortChange } = preferences;
  const { data, config, error, isLoading } = useTableResource(resource, preferences, errorHandlers);
  const selected = useSelector((state) => tableSelectedSelector(state, { resource }));

  const handleChange = (change) => {
    if (isLoading) {
      return;
    }

    onPageSizeChange(change.size);
    onSortChange(change.sort);
    dispatch(tableChange(resource, change));
  };

  const handleSelectAll = () => {
    dispatch(selectedChange(resource, selected.length > 0 ? [] : data?.map((i) => i.id)));
  };

  const handleSelectSingle = (selectedId) => {
    if (selected.includes(selectedId)) {
      dispatch(
        selectedChange(
          resource,
          selected.filter((s) => s !== selectedId)
        )
      );
    } else {
      dispatch(selectedChange(resource, [...selected, selectedId]));
    }
  };

  const handleRowClick = (data) => {
    navigate(
      resource.getDetailPath
        ? resource.getDetailPath(data)
        : generatePath(resource.detailPath, {
            [resource.detailPathParam || 'id']: data[resource.detailPathParam || 'id']
          })
    );
  };

  return (
    <Table
      resourceKey={resource.key}
      title={resource?.table?.title}
      legend={legend}
      highlight={resource?.table?.highlight}
      highlightType={resource?.table?.highlightType}
      emptyMessage={resource?.table?.emptyMessage}
      disableRow={resource?.table?.disableRow}
      responsive
      actions={actions}
      isLoading={isLoading || !isFetched}
      onChange={handleChange}
      onFieldEnableChange={onEnableField}
      onFieldOrderChange={onOrderField}
      onSelect={handleSelectSingle}
      onSelectAll={handleSelectAll}
      onRowClick={resource.detailPath || resource.getDetailPath ? handleRowClick : onRowClick || undefined}
      selected={selected}
      data={data}
      fields={fields}
      withPagination={!resource?.table?.disablePagination}
      enableSelect={resource?.table?.enableSelect}
      hasError={!!error}
      config={{ ...config, sort: sort || config.sort, size: size || config.size }}
    />
  );
};

export default ResourceTable;
